import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './ClipCard.module.scss';
import { Card } from '../Card';
import Avatar from '../Avatar/Avatar';
import CardThumbnail from '../Card/CardThumbnail';
import CardFooter from '../Card/CardFooter';
import CardTitle from '../Card/CardTitle';
import CardExtra from '../Card/CardExtra';
import DotsBtn from '../Menu/DotsBtn';
import Checkbox from '../Checkbox/Checkbox';
import { SortableHandle } from 'react-sortable-hoc';
import cx from 'classnames';
import { formatSecondsToDuration } from '../../utils/TimeUtil';
import ResourceHelper from '../../utils/ResourceHelper';
import { useTrimContext } from '../../context/VideoTrimContex';
import VideoService from '../../service/VideoService';
import { useBackgroundMusicContext } from '../../context/BackgroundMusicContext';
import BackgroundMusicThumbnail from '../BackgroundMusicEditor/BackgroundMusicThumbnail';
import { useZoomContext } from '../../context/ZoomContext';
import InfoTooltip from 'engage/features/Home/components/InfoTooltip/InfoTooltip';

const DragHandle = SortableHandle(({ children }) => {
  return children;
});

const ClipCard = ({
  videoId,
  clip,
  author,
  clipIndex,
  handleDotsClick,
  handleSelectClip,
  selectedClip,
  showCheckbox = false,
  showMenu,
  externalAddMenuShow,
  saving,
  disableHover,
  onResourceGenerated = null,
  currentJobId,
  updateJobId,
  cards,
  className,
  onBackgroundMusicChange,
  isAutoScroll,
  isEditingAction,
  handleHoverCard = () => { },
  showMinCard,
  thumbnailFallback,
}) => {
  const [hoverCard, setHoverCard] = useState(false);
  const [thumbnail, setThumbnail] = useState(null);

  const cardRef = useRef(null);

  const { trimmingOn } = useTrimContext();

  const { isBackgroundMusicOpened } = useBackgroundMusicContext();

  const { isZoomOpened } = useZoomContext();

  const generateThumbnail = useCallback(async () => {
    const response = await VideoService.checkSceneJobs(videoId, clip?.originalId ? clip.originalId : clip.id);
    if (response.hasRunningJobs && Array.isArray(response.runningJobs.outputs) && response.runningJobs.outputs.length > 0) {
      const thumbnailResourceJob = response.runningJobs.outputs.find(item => item.type === 'thumbnail');
      if (thumbnailResourceJob) {
        ResourceHelper.getJobDetails(thumbnailResourceJob.jobId, onResourceGenerated);
        updateJobId(thumbnailResourceJob.jobId);
        return;
      }
    }
  }, [onResourceGenerated, clip]);

  useEffect(() => {
    if (clip.thumbnail) {
      setThumbnail(clip.thumbnail);
    } else {
      let thumbnailResource = ResourceHelper.getResourceByLabel(clip, 'defaultStaticThumbnail');
      if (thumbnailResource !== undefined) {
        setThumbnail(thumbnailResource.url);
      } else {
        if (onResourceGenerated !== null && currentJobId === null) {
          generateThumbnail();
        }
      }
    }
  }, [clip]);

  const toggleMouseEnterCard = () => {
    !disableHover && setHoverCard(true), handleHoverCard(clipIndex);
  };

  const toggleMouseLeaveCard = () => {
    setHoverCard(false);
    //handleHoverCard(null);
  };

  const handleCardClip = useCallback(
    e => {
      if (showMenu?.flag && showMenu.id !== clip.id) {
        handleDotsClick();
      }
      if ((!((trimmingOn || isZoomOpened) && !clip.video) && !clip?.disabled)) {
        const isTargetCard =
          showCheckbox || Array.from(e.target.parentElement.classList).find(item => item.includes('Card')) !== undefined;
        if (isTargetCard && !clip.processing) {
          handleSelectClip(clipIndex.toString());
          // To keep hover state on card after click on card
          setTimeout(() => {
            handleHoverCard(clipIndex);
            setHoverCard(true);
          }, 10);
        }
      }
    },
    [showMenu, clip, clipIndex, handleSelectClip, handleDotsClick]
  );

  const onDotsClick = useCallback((event) => {
    handleDotsClick && handleDotsClick({ id: clip.id, event, index: clipIndex, isProcessing: clip?.processing, isDraft: !clip?.video && !clip.trimmedDuration  });
  }, [handleDotsClick, clip]);

  useEffect(() => {
    if (isAutoScroll && selectedClip && cardRef.current) {
      // Usual scrollIntoView scrolls both horizontally & vertically, but we need to scroll only horizontally
      // Scroll to the element only if element is not fully visible, and scroll to the point to only show the elements fully
      const fitToLeft = cardRef.current.offsetLeft >= cardRef.current.parentElement.scrollLeft;
      const fitToRight = cardRef.current.offsetLeft + cardRef.current.offsetWidth <= cardRef.current.parentElement.scrollLeft + cardRef.current.parentElement.clientWidth;
      if (fitToRight && fitToLeft) {
        return;
      }


      let scrollLeft = 0;
      if (!fitToLeft) {
        scrollLeft = cardRef.current.offsetLeft;
      }
      if (!fitToRight) {
        const offset = cardRef.current.offsetWidth - (cardRef.current.parentElement.scrollLeft + cardRef.current.parentElement.clientWidth - cardRef.current.offsetLeft);
        scrollLeft = cardRef.current.parentElement.scrollLeft + offset;
      }

      cardRef.current.parentElement.scrollTo({
        left: scrollLeft,
        behavior: 'smooth',
        inline: 'nearest', block: 'nearest'
      });
    }
  }, [selectedClip]);

  return (
    <div ref={cardRef} className={cx(styles.listCol, className)}>
      <Card
        className={cx(styles.clipCard, {
          [styles.activeClip]: selectedClip && !clip.processing,
          [styles.processing]: clip.processing || saving,
          [styles.disabled]: ((trimmingOn || isZoomOpened) && !clip.video) || clip.disabled,
        })}
        childrenClassName={styles.clipCardChildren}
        onMouseEnter={toggleMouseEnterCard}
        onMouseLeave={toggleMouseLeaveCard}
        onMouseDown={handleCardClip}
      >
        <DragHandle>
          <div className={cx(styles.thumbnailTitleContainer, { [styles.fullHeight]: showMinCard })}>
            <CardThumbnail
              className={cx(styles.cardThumbnail, { [styles.processing]: clip.processing })}
              listMode={false}
              src={clip?.recording ? null : thumbnail}
              alt="Click to Drag"
              isDraft={clip.video === null || clip?.recording}
              showDraftStatus={false}
              processing={clip.processing}
              fallbackSvg={thumbnailFallback}
            />
            <CardTitle
              className={cx(styles.title, {
                [styles.showTitle]: hoverCard || (showCheckbox && selectedClip),
                [styles.minCard]: showMinCard,
                [styles.musicThumbnail]: isBackgroundMusicOpened && onBackgroundMusicChange,
                [styles.allEvents]: showMenu?.flag || externalAddMenuShow,
                [styles.fullHeight]: showMinCard,
              })}
              stylesForSpan={{
                ...(isBackgroundMusicOpened && onBackgroundMusicChange && { overflow: 'inherit' }),
              }}
            >
              {isBackgroundMusicOpened && onBackgroundMusicChange && clip?.id !== 'Intro' && clip?.id !== 'Outro' ? (
                <BackgroundMusicThumbnail clip={clip} clipIndex={clipIndex} onBackgroundMusicChange={onBackgroundMusicChange} />
              ) : (
                clip.name ?? clip.title
              )}
              {showMinCard && (clip?.trimmedDuration || clip?.duration) && !clip?.rerecording && (
                <div className={cx(styles.duration, styles.minCard, styles.leftPositioned)}>
                  {formatSecondsToDuration(
                    (clip?.trimmedDuration ? clip?.trimmedDuration : clip.duration) / (clip?.playbackRate || 1) / 1000
                  )}
                </div>
              )}
            </CardTitle>
          </div>
        </DragHandle>
        {(hoverCard && showMinCard && !isEditingAction && handleDotsClick) ? (
          <DotsBtn
            tagName="BUTTON"
            disabled={cards.length === 1 && clip.processing}
            className={cx(styles.minDotsBtn, { [styles.disabled]: cards.length === 1 && clip.processing })}
            onClick={onDotsClick}
          />
        ) : null}
        {!showMinCard && (
          <CardFooter className={styles.footerCard}>
            {showCheckbox && (hoverCard || selectedClip) && <Checkbox checked={selectedClip} />}
            {(!showCheckbox || (showCheckbox && !hoverCard && !selectedClip)) && (
              <Avatar className={styles.miniUserDetail} image={author?.avatar} title={author?.name} size="small" alt={`Video's owner avatar`} />
            )}
            {(clip?.trimmedDuration || clip?.duration) && (
              <div className={styles.duration}>
                {formatSecondsToDuration(
                  (clip?.trimmedDuration ? clip?.trimmedDuration : clip.duration) / (clip?.playbackRate || 1) / 1000
                )}
              </div>
            )}
            {(handleDotsClick && !isEditingAction) ? (
              <CardExtra>
                {!trimmingOn && (hoverCard || (showMenu?.flag && showMenu?.id === clip.id)) ? (
                  <DotsBtn
                    tagName="BUTTON"
                    disabled={cards.length === 1 && clip.processing}
                    className={cx(styles.dotsBtn, { [styles.disabled]: cards.length === 1 && clip.processing })}
                    onClick={onDotsClick}
                  />
                ) : null}
              </CardExtra>
            ) : null}
            {clip?.info?.text && <InfoTooltip containerRef={cardRef} text={clip?.info?.text} icon={clip?.info?.icon} fixedPosition={true} />}
          </CardFooter>
        )}
      </Card>
    </div>
  );
};

export default ClipCard;
